import { IntlShape } from "react-intl";

/**
 * Color palette and utilities for ProposalProducts
 * These colors are designed to be easily distinguishable from each other
 * and from the cutting quality colors:
 * - Max 72 (Regular): #4ECDC4 (cyan/teal)
 * - Standard 48 (Pro): #FFE66D (yellow)
 * - Sport 24 (ProSport): #FF6B6B (red/orange)
 */

interface ColorInfo {
  hex: string;
  class: string;
}

// Colors sorted to maximize visual difference between adjacent colors
// When cycling through the palette, each next color will be visually distinct from the previous
const COLOR_DEFINITIONS: ColorInfo[] = [
  // Light purple/violet
  { hex: "#BE4FFF", class: "violet-light" },
  
  // Bright blue
  { hex: "#2299FF", class: "blue-bright" },
  
  // Pink
  { hex: "#FF90C7", class: "pink" },
  
  // Blue
  { hex: "#2E2EFF", class: "blue" },
  
  // Pink/magenta
  { hex: "#E91E63", class: "pink-deep" },
  
  // Yellow-green
  { hex: "#9ACD32", class: "yellow-green" },
  
  // Medium slate blue
  { hex: "#7B68EE", class: "slate-blue" },
  
  // Magenta/fuchsia
  { hex: "#FF00FF", class: "magenta" },
  
  // Spring green
  { hex: "#00FF80", class: "spring-green" },
  
  // Red
  { hex: "#E73737", class: "red" },
  
  // Turquoise
  { hex: "#40E0D0", class: "turquoise" },
  
  // Deep pink
  { hex: "#FF4081", class: "pink-hot" },
  
  // Dark violet
  { hex: "#6C0FB1", class: "violet-dark" },
  
  // Light cyan
  { hex: "#96ECEC", class: "cyan-light" },
  
  // Deep pink/red
  { hex: "#DF2248", class: "crimson" },
  
  // Bright green
  { hex: "#29D429", class: "green-bright" },
  
  // Deep pink (CSS named color)
  { hex: "#FF1493", class: "deep-pink" },
  
  // Dark blue
  { hex: "#0000BD", class: "blue-dark" },
  
  // Green
  { hex: "#19C461", class: "green" },
  
  // Orchid
  { hex: "#DA70D6", class: "orchid" },
  
  // Tomato red
  { hex: "#FF6347", class: "tomato" },
  
  // Dark violet
  { hex: "#9400D3", class: "violet-deep" },
  
  // Chartreuse
  { hex: "#DDFF00", class: "chartreuse" },
  
  // Medium violet red
  { hex: "#C71585", class: "violet-red" },
  
  // Tan/beige
  { hex: "#C19E85", class: "tan" },
  
  // Orange-red
  { hex: "#E83F00", class: "orange-red" },
];

export const PROPOSAL_PRODUCT_COLORS = COLOR_DEFINITIONS.map(c => c.hex) as readonly string[];

export const COLOR_TO_CLASS_MAP: Record<string, string> = Object.fromEntries(
  COLOR_DEFINITIONS.map(c => [c.hex, c.class])
) as Record<string, string>;

/**
 * Default color translations in English
 * These are used as fallback values and to ensure Format.JS can extract them
 * Format.JS will find these through the getColorTranslationDefaults function below
 */
export const COLOR_TRANSLATION_DEFAULTS: Record<string, string> = {
  "color.violet-light": "Light violet",
  "color.blue-bright": "Bright blue",
  "color.pink": "Pink",
  "color.blue": "Blue",
  "color.pink-deep": "Deep pink",
  "color.yellow-green": "Yellow green",
  "color.slate-blue": "Slate blue",
  "color.magenta": "Magenta",
  "color.spring-green": "Spring green",
  "color.red": "Red",
  "color.turquoise": "Turquoise",
  "color.pink-hot": "Hot pink",
  "color.violet-dark": "Dark violet",
  "color.cyan-light": "Light cyan",
  "color.crimson": "Crimson",
  "color.green-bright": "Bright green",
  "color.deep-pink": "Deep pink",
  "color.blue-dark": "Dark blue",
  "color.green": "Green",
  "color.orchid": "Orchid",
  "color.tomato": "Tomato",
  "color.violet-deep": "Deep violet",
  "color.chartreuse": "Chartreuse",
  "color.violet-red": "Violet red",
  "color.tan": "Tan",
  "color.orange-red": "Orange red",
};

/**
 * This function contains all color translation keys as static formatMessage calls
 * Format.JS will extract these when scanning the codebase
 * This function is never actually called, it's only here for extraction purposes
 */
export const getColorTranslationDefaults = (intl: IntlShape) => {
  // Format.JS will extract these static formatMessage calls
  intl.formatMessage({ id: "color.violet-light", defaultMessage: "Light violet" });
  intl.formatMessage({ id: "color.blue-bright", defaultMessage: "Bright blue" });
  intl.formatMessage({ id: "color.pink", defaultMessage: "Pink" });
  intl.formatMessage({ id: "color.blue", defaultMessage: "Blue" });
  intl.formatMessage({ id: "color.pink-deep", defaultMessage: "Deep pink" });
  intl.formatMessage({ id: "color.yellow-green", defaultMessage: "Yellow green" });
  intl.formatMessage({ id: "color.slate-blue", defaultMessage: "Slate blue" });
  intl.formatMessage({ id: "color.magenta", defaultMessage: "Magenta" });
  intl.formatMessage({ id: "color.spring-green", defaultMessage: "Spring green" });
  intl.formatMessage({ id: "color.red", defaultMessage: "Red" });
  intl.formatMessage({ id: "color.turquoise", defaultMessage: "Turquoise" });
  intl.formatMessage({ id: "color.pink-hot", defaultMessage: "Hot pink" });
  intl.formatMessage({ id: "color.violet-dark", defaultMessage: "Dark violet" });
  intl.formatMessage({ id: "color.cyan-light", defaultMessage: "Light cyan" });
  intl.formatMessage({ id: "color.crimson", defaultMessage: "Crimson" });
  intl.formatMessage({ id: "color.green-bright", defaultMessage: "Bright green" });
  intl.formatMessage({ id: "color.deep-pink", defaultMessage: "Deep pink" });
  intl.formatMessage({ id: "color.blue-dark", defaultMessage: "Dark blue" });
  intl.formatMessage({ id: "color.green", defaultMessage: "Green" });
  intl.formatMessage({ id: "color.orchid", defaultMessage: "Orchid" });
  intl.formatMessage({ id: "color.tomato", defaultMessage: "Tomato" });
  intl.formatMessage({ id: "color.violet-deep", defaultMessage: "Deep violet" });
  intl.formatMessage({ id: "color.chartreuse", defaultMessage: "Chartreuse" });
  intl.formatMessage({ id: "color.violet-red", defaultMessage: "Violet red" });
  intl.formatMessage({ id: "color.tan", defaultMessage: "Tan" });
  intl.formatMessage({ id: "color.orange-red", defaultMessage: "Orange red" });
};

/**
 * Gets the CSS class name for a color
 * @param color Hex color code
 * @returns CSS class name for the color, or "grey" as default
 */
export const getColorClassForColor = (color: string | undefined | null): string => {
  if (!color) return "grey";
  
  // Check if we have a mapping for this color
  const colorClass = COLOR_TO_CLASS_MAP[color];
  if (colorClass) {
    return colorClass;
  }
  
  // Default to grey if color is not recognized
  return "grey";
};

/**
 * Gets the alt text/description for a color using intl translations
 * @param color Hex color code
 * @param intl IntlShape instance from react-intl
 * @returns Alt text for the color, or empty string if not found
 */
export const getColorAltText = (color: string | undefined | null, intl: IntlShape): string => {
  if (!color || !intl) return "";
  
  const colorClass = getColorClassForColor(color);
  if (!colorClass) return "";
  
  const translationKey = `color.${colorClass}`;
  const defaultMessage = COLOR_TRANSLATION_DEFAULTS[translationKey] || "";
  return intl.formatMessage({ id: translationKey, defaultMessage });
};

/**
 * Alias for getColorAltText for backward compatibility
 */
export const getColorAlt = getColorAltText;

/**
 * Gets the next available color for a new proposal product
 * Cycles through the color palette, avoiding colors already in use
 * @param usedColors Array of hex color codes that are already in use by other proposalProducts
 * @returns Hex color code for the next available color that's not in use.
 *          If all colors are used, returns the least recently used color (cycles through palette).
 */
export const getNextAvailableColor = (usedColors: string[]): string => {
  // Normalize used colors: convert to uppercase and filter out empty/null/undefined values
  const normalizedUsedColors = usedColors
    .filter((color): color is string => !!color)
    .map(color => color.toUpperCase().trim());
  
  // Cutting quality colors that should be avoided (reserved for cutting quality display)
  const cuttingQualityColors = ["#4ECDC4", "#FFE66D", "#FF6B6B"].map(c => c.toUpperCase());
  const proposalProductUsedColors = normalizedUsedColors.filter(
    color => !cuttingQualityColors.includes(color)
  );
  
  // Create a Set for O(1) lookup performance
  const usedColorsSet = new Set(proposalProductUsedColors);
  
  // Find the first color in our palette that's not in use
  for (const color of PROPOSAL_PRODUCT_COLORS) {
    const normalizedColor = color.toUpperCase();
    if (!usedColorsSet.has(normalizedColor)) {
      return color;
    }
  }
  
  // If all colors in the palette are used (more than 26 proposalProducts),
  // we cycle through the palette using the number of used colors as an index.
  // This ensures we distribute colors evenly when we have more products than colors.
  // The modulo operation ensures we always get a valid index within the array bounds.
  const usedCount = proposalProductUsedColors.length;
  const index = usedCount % PROPOSAL_PRODUCT_COLORS.length;
  const selectedColor = PROPOSAL_PRODUCT_COLORS[index];
  
  // PROPOSAL_PRODUCT_COLORS is guaranteed to have at least one element (26 colors defined)
  // This fallback should never be needed, but TypeScript requires it for type safety
  return (selectedColor ?? PROPOSAL_PRODUCT_COLORS[0] ?? "#BE4FFF") as string;
};
